import { subscriptionTypes } from '@constants/index';
import textHelper from '@utils/textHelper';

export type PlanTagType = number | string | undefined;

interface TaxBreakdownItem {
  amount: number;
  label: string;
}

export interface BundleSubscription {
  uid: string;
  type?: number;
  tax_breakdown?: TaxBreakdownItem[];
  bundle_saved_amount?: number;
  [key: string]: unknown;
}

export interface BundleAddOn {
  uid: string;
  name: string;
  add_for: number;
  originally_for: number;
  is_referral_invalid?: boolean;
  topology_details?: Array<{
    uid: string;
    type: string;
    name: string;
    title?: string;
  }>;
  subscription?: BundleSubscription;
  isMandatory?: boolean;
  mandatoryBundles?: BundleAddOn[];
  [key: string]: unknown;
}

export const isIconicProSubscription = (
  subscription?: { type?: number } | null
) => subscription?.type === subscriptionTypes.ICONIC_PRO;

export interface ComboTopology {
  type?: number | string;
  name?: string;
  uid?: string;
}

const isPlusTopologyType = (type?: ComboTopology['type']) =>
  type === subscriptionTypes.PLUS || type === 'Plus';

const isIconicProTopologyType = (type?: ComboTopology['type']) =>
  type === subscriptionTypes.ICONIC_PRO || type === 'Iconic Pro';

const isIconicTopologyType = (type?: ComboTopology['type']) =>
  type === subscriptionTypes.ICONIC || type === 'Iconic';

export const getComboGoalsByTier = (topologies: ComboTopology[] = []) => {
  let plusGoals = '';
  let iconicGoals = '';
  let iconicProGoals = '';

  topologies.forEach(({ type, name = '' }) => {
    if (isPlusTopologyType(type)) {
      plusGoals += `${plusGoals ? ' + ' : ''}${name}`;
    } else if (isIconicProTopologyType(type)) {
      iconicProGoals += `${iconicProGoals ? ' + ' : ''}${name}`;
    } else if (isIconicTopologyType(type)) {
      iconicGoals += `${iconicGoals ? ' + ' : ''}${name}`;
    }
  });

  return {
    plusGoals,
    iconicGoals,
    iconicProGoals
  };
};

/**
 * Returns combo goals in API topologies order (one row per topology).
 */
export const getOrderedComboGoalRows = (
  topologies: ComboTopology[] = []
): Array<{ type: PlanTagType; name: string; uid?: string }> =>
  (topologies || [])
    .filter(
      (topology) =>
        isPlusTopologyType(topology.type) ||
        isIconicTopologyType(topology.type) ||
        isIconicProTopologyType(topology.type)
    )
    .map(({ type, name = '', uid }) => ({ type, name, uid }));

export type MwebBundleSummaryItem = {
  key: string;
  title: string;
  displayDuration?: string;
  endsAt?: string | Date | null;
  taxBreakdown?: TaxBreakdownItem[] | null;
  showPrice: boolean;
};

/**
 * Mweb payment upper-section line items, built from childSubscriptions.
 */
export const getMwebBundleSummaryItems = ({
  subscription
}: {
  subscription?: {
    childSubscriptions?: Array<{
      uid?: string;
      title?: string;
      displayDuration?: string;
      checkoutDatesWithBenefits?: { endsAt?: string };
      taxBreakdown?: TaxBreakdownItem[];
    }>;
  } | null;
}): MwebBundleSummaryItem[] => {
  const childSubscriptions = subscription?.childSubscriptions || [];
  return childSubscriptions.map((child, index) => ({
    key: child.uid || `child-${index}`,
    title: child.title || '',
    displayDuration: child.displayDuration,
    endsAt: child.checkoutDatesWithBenefits?.endsAt,
    taxBreakdown: child.taxBreakdown,
    showPrice: true
  }));
};

export interface ComboDisplayLineItem {
  key: string;
  type: PlanTagType;
  name: string;
  price: number;
  originalPrice?: number;
  priceLabel?: string;
}

const getTaxBreakdownTotal = (taxBreakdown?: TaxBreakdownItem[]) =>
  taxBreakdown?.find((item) => item.label === 'Total')?.amount ?? 0;

export const getPrimaryComboLineItem = (
  subscription: BundleSubscription | undefined,
  primaryGoalName: string
): ComboDisplayLineItem => ({
  key: subscription?.uid || 'primary-subscription',
  type: subscription?.type,
  name: primaryGoalName,
  price: getTaxBreakdownTotal(subscription?.tax_breakdown),
  priceLabel: 'Subscription price'
});

export const getBundleLineItems = (
  bundle: BundleAddOn
): ComboDisplayLineItem[] => {
  const childSubscriptions = (bundle.subscription?.child_subscriptions ||
    bundle.subscription?.childSubscriptions) as
    | Array<{
        uid: string;
        type: number;
        title?: string;
        goal?: { name?: string; uid?: string };
        tax_breakdown?: TaxBreakdownItem[];
        price?: string | number;
      }>
    | undefined;

  if (childSubscriptions?.length) {
    return childSubscriptions.map((child) => ({
      key: child.uid,
      type: child.type,
      name: child.goal?.name || child.title || '',
      price: getTaxBreakdownTotal(child.tax_breakdown),
      originalPrice: child.price ? Number(child.price) : undefined
    }));
  }

  const topologies = bundle.topology_details || [];

  if (topologies.length <= 1) {
    const goal = topologies[0];
    return [
      {
        key: bundle.uid || goal?.uid || 'addon-bundle',
        type: goal?.type,
        name: goal?.name || bundle.name,
        price: bundle.add_for || 0,
        originalPrice: bundle.originally_for
      }
    ];
  }

  const perAddFor = (bundle.add_for || 0) / topologies.length;
  const perOriginal = (bundle.originally_for || 0) / topologies.length;

  return topologies.map((goal, index) => ({
    key: goal.uid || `${bundle.uid}-${index}`,
    type: goal.type,
    name: goal.name,
    price: perAddFor,
    originalPrice: perOriginal
  }));
};

export const getAddedBundleLineItems = (
  addedBundle?: BundleAddOn | null
): ComboDisplayLineItem[] => {
  if (!addedBundle) return [];

  if (addedBundle.mandatoryBundles?.length) {
    return addedBundle.mandatoryBundles.flatMap(getBundleLineItems);
  }

  return getBundleLineItems(addedBundle);
};

export const getValidBundleAddOns = (bundles?: BundleAddOn[] | null) =>
  (bundles || []).filter((bundle) => !bundle.is_referral_invalid);

/**
 * Iconic Pro: auto-select only when exactly one valid bundle (forced, non-removable).
 * Multiple bundles stay optional until Continue (which falls back to the first).
 */
export const getIconicProAutoSelectedBundle = (
  bundles?: BundleAddOn[] | null
): BundleAddOn | null => {
  const validBundles = getValidBundleAddOns(bundles);
  if (validBundles.length !== 1) return null;
  return { ...validBundles[0], isMandatory: true };
};

/** Iconic Pro: first valid bundle when user continues without picking one. */
export const getIconicProDefaultBundleOnProceed = (
  bundles?: BundleAddOn[] | null
): BundleAddOn | null => {
  const validBundles = getValidBundleAddOns(bundles);
  return validBundles[0] || null;
};

/**
 * Bundle topology "shape" decides which combo-picker UI variant to render:
 * - a bundle whose topology spans more than one goal is a "combo" (Variant A)
 * - bundles with a single (or no) topology entry are plain single-goal add-ons (Variant B)
 */
export const hasMultiGoalBundleAddOns = (bundles?: BundleAddOn[] | null) =>
  (bundles || []).some((bundle) => (bundle.topology_details?.length || 0) > 1);

export const getBundleAddCtaLabel = (
  bundle?: { add_for?: number } | null,
  currency = ''
) => {
  if (isComplimentaryBundleAddOn(bundle)) return 'Add for free';
  return `Add for ${currency}${textHelper.commas(
    Math.round(bundle?.add_for || 0)
  )}`;
};

export const getTopologyTypeLabel = (type: PlanTagType): string => {
  if (typeof type === 'string') return type;
  if (type === subscriptionTypes.PLUS) return 'Plus';
  if (type === subscriptionTypes.ICONIC_PRO) return 'Iconic Pro';
  if (type === subscriptionTypes.ICONIC) return 'Iconic';
  return '';
};

export const getPlanTypeTagImage = (
  type: PlanTagType,
  isMobile = false
): string => {
  if (type === subscriptionTypes.PLUS || type === 'Plus') {
    return isMobile ? 'bundles/plusTag.png' : 'bundles/plusTagSmall.png';
  }
  if (type === subscriptionTypes.ICONIC_PRO || type === 'Iconic Pro') {
    return isMobile ? 'bundles/iconicProTag.png' : 'bundles/iconicProSmall.png';
  }
  return isMobile ? 'bundles/iconicTag.png' : 'bundles/iconicTagSmall.png';
};

export const getPlanTagDimensions = (
  isMobile = false
): { width: number; height: number } =>
  isMobile ? { width: 86, height: 24 } : { width: 86, height: 28 };

export const isComplimentaryBundleAddOn = (
  bundle?: {
    add_for?: number;
  } | null
) => bundle?.add_for === 0;

export const getAddOnPriceLabel = (
  bundle: { add_for?: number; originally_for?: number } | null | undefined,
  currency: string
) => {
  if (isComplimentaryBundleAddOn(bundle)) {
    return `${currency}${textHelper.commas(
      Math.round(bundle?.originally_for || 0)
    )}`;
  }
  return 'Add-on price';
};
